import type { ITransformRT2, IVector3 } from '@seddi/math-v2';
import {
  type Button,
  type GarmentGeometry,
  type IGarmentPieceComponent,
  type ISceneEntity,
  type PieceButton,
  type PieceButtonHole,
  meshOrientedSpline,
} from '@seddi/scene';
import {
  BufferGeometry,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  Quaternion,
  Vector3,
} from '@seddi/three';
import type { VertexDataDesc } from '../core/bufferTypes';
import { computeTransformBasis } from '../geometry/triMeshTransformBasis';
import type { ResourcesContext } from '../resources/resourcesContext';
import { SceneGeometryModule } from '../scene/sceneGeometryModule';
import {
  type StitchRenderable,
  create as createStitch,
  dispose as disposeStitch,
} from './stitchRenderable';

export interface PieceButtonRenderable {
  readonly id: string;
  /**
   * Garment geometry ref.
   */
  garmentGeometry?: GarmentGeometry;
  isOutOfBounds?: boolean;
  readonly mesh: Mesh;
  /**
   * Button ref.
   */
  resourceRef?: string;
  readonly stitches: StitchRenderable[];
}

interface Basis {
  normal: Vector3;
  position: Vector3;
  tangent: Vector3;
}

interface ParentRenderable {
  mesh: Mesh;
}

/**
 * Create.
 * @param id - Piece button ID.
 * @param parent - Parent.
 */
export const create = (id: string, parent: Object3D): PieceButtonRenderable => {
  const mesh = new Mesh(new BufferGeometry(), new MeshStandardMaterial());
  mesh.name = `${id}-mesh`;
  parent.add(mesh);
  return { id, mesh, stitches: [] };
};

/**
 * Dispose.
 * @param renderable - Renderable.
 * @param resources - Resources context.
 */
export const dispose = (renderable: PieceButtonRenderable, resources: ResourcesContext) => {
  detachButton(renderable, resources);
  const mesh = renderable.mesh;
  mesh.parent.remove(mesh);
};

/**
 * Find parent entity ID.
 * @param renderable - Renderable.
 */
export const getParentId = (renderable: PieceButtonRenderable) => {
  return renderable.mesh.parent.name;
};

/**
 * Update transform.
 * @param renderable - Renderable.
 * @param garmentGeometry - Garment geometry.
 */
export const updateTransformFromGarment = (
  renderable: PieceButtonRenderable,
  garmentGeometry: GarmentGeometry,
) => {
  const button = garmentGeometry.buttons.find((b) => b.id === renderable.id);
  const mesh = renderable.mesh;
  const { translation, rotation } = button.transform;
  const q = _q;
  q.set(rotation.x, rotation.y, rotation.z, rotation.w);
  mesh.setRotationFromQuaternion(q);
  mesh.position.set(translation.x, translation.y, translation.z);
  mesh.updateMatrix();
};

/**
 * Update transform.
 * @param renderable - Renderable.
 * @param descriptor - Button or hole descriptor.
 * @param resource - Resource.
 * @param pieceRenderable - Button or hole piece renderable.
 * @param piece - Button or hole piece descriptor,
 */
export const updateTransformFromPiece = (
  renderable: PieceButtonRenderable,
  descriptor: PieceButton | PieceButtonHole,
  resource: Button,
  pieceRenderable: ParentRenderable,
  piece: ISceneEntity,
) => {
  const { mesh } = renderable;
  const { transform } = descriptor;

  const b0 = _b0;
  computeMeshTransformBasis(piece.components.garmentPiece!, pieceRenderable.mesh, transform, b0);

  const tangent2 = _v;
  const m = _m4;
  const q = _q;
  tangent2.crossVectors(b0.normal, b0.tangent);
  m.makeBasis(b0.tangent, tangent2, b0.normal);
  q.setFromRotationMatrix(m);

  // Button placement depends on button height.
  const position = b0.position;
  position.add(b0.normal.normalize().multiplyScalar(resource!.bounds.height / 2));

  mesh.setRotationFromQuaternion(q);
  mesh.position.copy(b0.position);
};

/**
 * Compute basis 3D.
 * @param piece - Piece.
 * @param pieceMesh - Piece mesh.
 * @param transform - Transform.
 * @param ref - Target basis.
 */
export const computeMeshTransformBasis = (
  piece: IGarmentPieceComponent,
  pieceMesh: Mesh,
  transform: ITransformRT2,
  ref: Basis,
) => {
  const data = adaptParentGeometry(piece, pieceMesh);
  // Update 2D query acceleration structures
  const pieceId = pieceMesh.parent.name;
  const geometryCache = SceneGeometryModule.instance.geometryCache;
  const geometryCachePiece = geometryCache.touchPiece(pieceId);
  // TODO [Ivan]: Get cell size from scene resolution.
  const cellSize = 0.015;
  geometryCachePiece.updateGrid(data.indices as Uint32Array, data.uvs, cellSize);
  // Compute basis.
  computeTransformBasis(data, geometryCachePiece.grid, transform, ref);
};

/**
 * Attach button.
 * @param renderable - Renderable.
 * @param descriptor - Descriptor.
 * @param resources - Resources context.
 */
export const attachButton = (
  renderable: PieceButtonRenderable,
  descriptor: PieceButton,
  resources: ResourcesContext,
) => {
  if (renderable.resourceRef) {
    detachButton(renderable, resources);
  }

  const id = descriptor.resourceID;
  const resource = resources.getRefButton(id)!;
  renderable.mesh.geometry = resource.geometry;
  renderable.mesh.material = resource.material;
  renderable.resourceRef = id;
};

/**
 * Detach button.
 * @param renderable - Renderable.
 * @param resources - Resources context.
 */
export const detachButton = (renderable: PieceButtonRenderable, resources: ResourcesContext) => {
  if (!renderable.resourceRef) {
    return;
  }
  resources.retRefButton(renderable.resourceRef);
  delete renderable.resourceRef;
};

/**
 * Create stitches.
 * @param renderable - Renderable.
 * @param process - Process.
 * @param garmentGeometry - Garment geometry.
 * @param resources - Resources context.
 */
export const createStitches = (
  renderable: PieceButtonRenderable,
  color: IVector3,
  garmentGeometry: GarmentGeometry,
  resources: ResourcesContext,
) => {
  // assert(renderable.stitches.length === 0);
  const button = garmentGeometry.buttons.find((op) => op.id === renderable.id);
  for (const buttonStitch of button.stitch) {
    const { name } = buttonStitch;
    const geometryData = meshOrientedSpline(buttonStitch, 3);
    const stitch = createStitch(renderable.mesh.parent, geometryData, color, resources);
    stitch.mesh.name = `${renderable.id}-stitch-${name}`;
    renderable.stitches.push(stitch);
  }
};

/**
 * Dispose stitches.
 * @param renderable - Renderable.
 */
export const disposeStitches = (renderable: PieceButtonRenderable) => {
  for (const stitch of renderable.stitches) {
    disposeStitch(stitch);
  }
  renderable.stitches.length = 0;
};

/**
 * Return true if piece and hole are closer than threshold.
 * @param buttonPiece - Button piece.
 * @param buttonMesh - Button piece mesh.
 * @param buttonTransform - Button transform.
 * @param holePiece - Hole piece.
 * @param holeMesh - Hole piece mesh.
 * @param holeTransform - Hole transform.
 */
export const isCloseToHole = (
  buttonPiece: IGarmentPieceComponent,
  buttonMesh: Mesh,
  buttonTransform: ITransformRT2,
  holePiece: IGarmentPieceComponent,
  holeMesh: Mesh,
  holeTransform: ITransformRT2,
): boolean => {
  const a = _b0;
  const b = _b1;
  computeMeshTransformBasis(buttonPiece, buttonMesh, buttonTransform, a);
  computeMeshTransformBasis(holePiece, holeMesh, holeTransform, b);
  return a.position.distanceTo(b.position) <= MIN_DISTANCE_BUTTON_OVERLAP_BUTTON_HOLE;
};

// Detail.

const MIN_DISTANCE_BUTTON_OVERLAP_BUTTON_HOLE = 0.02; // 2cm

/**
 * Adapt geometry.
 * @param piece - Piece descriptor.
 * @param mesh - Piece mesh with computed normals.
 */
const adaptParentGeometry = (piece: IGarmentPieceComponent, mesh: Mesh): VertexDataDesc => {
  const { geometry } = piece;
  const data: VertexDataDesc = {
    indices: geometry.indices,
    positions: geometry.position3D,
    // TODO: Do not retrieve computed normals directly from the mesh.
    normals: mesh.geometry.getAttribute('normal').array as Float32Array,
    uvs: geometry.position2D,
  };
  return data;
};

const _b0 = { normal: new Vector3(), position: new Vector3(), tangent: new Vector3() };
const _b1 = { normal: new Vector3(), position: new Vector3(), tangent: new Vector3() };
const _m4 = new Matrix4();
const _q = new Quaternion();
const _v = new Vector3();
